import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, Edit2 } from 'lucide-react';
import { authService } from '../services/authService';
import MasterAdminDashboard from './MasterAdminDashboard';

export default function Dashboard() {
  const { currentUser, userRole, loading, error } = useAuth();
  const navigate = useNavigate();
  const [showChangeDisplayName, setShowChangeDisplayName] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [displayNameError, setDisplayNameError] = useState('');
  const [displayNameLoading, setDisplayNameLoading] = useState(false);
  const [canChangeDisplayName, setCanChangeDisplayName] = useState(true);
  const [nextChangeDate, setNextChangeDate] = useState(null);
  const [userData, setUserData] = useState(null);

  // Load user data and check display name change availability
  useEffect(() => {
    const loadUserData = async () => {
      try {
        if (!currentUser) return;

        const data = await authService.getUserData(currentUser.uid);
        setUserData(data);

        if (data?.lastDisplayNameChange) {
          const lastChange = new Date(
            data.lastDisplayNameChange.toDate
              ? data.lastDisplayNameChange.toDate()
              : data.lastDisplayNameChange
          );
          const nextChange = new Date(lastChange.getTime() + 30 * 24 * 60 * 60 * 1000);
          const now = new Date();

          if (now < nextChange) {
            setCanChangeDisplayName(false);
            setNextChangeDate(nextChange);
          }
        }
      } catch (err) {
        console.error('Error loading user data:', err);
      }
    };

    loadUserData();
  }, [currentUser]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-4">🔥</div>
          <p className="text-xl">Loading...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error || userRole === 'error') {
    const errorMessage = error || 'Could not load user role due to a permission issue.';
    const isPermissionError = errorMessage.includes('Missing or insufficient permissions');
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-4">⚠️</div>
          <p className="text-2xl font-bold text-red-500 mb-4">Dashboard Access Error</p>
          <p className="text-gray-400 max-w-md mx-auto mb-6">{errorMessage}</p>

          {isPermissionError && (
            <div className="bg-zinc-900 border border-red-500/30 p-6 rounded-lg text-left inline-block max-w-xl mb-6">
              <h3 className="font-bold text-lg text-red-400 mb-3">
                Action Required: Update Firestore Rules
              </h3>
              <p className="text-gray-300 text-sm mb-4">
                Your database security rules need to be updated before you can access the dashboard.
              </p>
              <div className="bg-black p-4 rounded-md font-mono text-sm text-green-400 mb-4">
                firebase deploy --only firestore:rules
              </div>
              <p className="text-gray-400 text-sm">
                Run the command above in your project terminal, then refresh this page.
              </p>
            </div>
          )}

          <div>
            <button
              onClick={async () => {
                await authService.signout();
                window.location.reload();
              }}
              className="bg-zinc-800 hover:bg-zinc-700 px-6 py-2 rounded-lg transition mr-4"
            >
              Sign Out
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-orange-600 hover:bg-orange-700 px-6 py-2 rounded-lg transition"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Not logged in
  if (!currentUser) {
    navigate('/signin');
    return null;
  }

  // Wait for role to be loaded
  if (!userRole) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-4">🔥</div>
          <p className="text-xl">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // =============================================
  // ROLE-BASED DASHBOARD ROUTING
  // =============================================

  // Master Admin and Admin both get the admin dashboard
  if (userRole === 'master_admin' || userRole === 'admin') {
    return <MasterAdminDashboard currentUser={currentUser} />;
  }

  // Moderator dashboard (add ModeratorDashboard component later)
  if (userRole === 'moderator') {
    return <MasterAdminDashboard currentUser={currentUser} />;
    // Replace above with <ModeratorDashboard currentUser={currentUser} /> when ready
  }

  // =============================================
  // MEMBER DASHBOARD (default)
  // =============================================

  const handleSignOut = async () => {
    await authService.signout();
    navigate('/');
  };

  const handleChangeDisplayName = async () => {
    if (!newDisplayName.trim()) {
      setDisplayNameError('Display name cannot be empty');
      return;
    }
    if (newDisplayName.length < 3) {
      setDisplayNameError('Display name must be at least 3 characters');
      return;
    }
    if (newDisplayName.length > 30) {
      setDisplayNameError('Display name cannot exceed 30 characters');
      return;
    }

    setDisplayNameLoading(true);
    setDisplayNameError('');

    try {
      await authService.updateDisplayName(currentUser.uid, newDisplayName);
      setShowChangeDisplayName(false);
      setNewDisplayName('');
      setCanChangeDisplayName(false);
      setNextChangeDate(new Date(Date.now() + 30 * 24 * 60 * 60 * 1000));
      window.location.reload();
    } catch (err) {
      setDisplayNameError(err.message || 'Failed to update display name');
    } finally {
      setDisplayNameLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Navigation */}
      <nav className="bg-zinc-900 border-b border-zinc-800 p-6">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition">
            <img
              src="/images/fhni%20logo.png"
              alt="Fire Hive Network"
              className="h-8 w-8 object-contain"
            />
            <span className="text-2xl font-black">FIRE HIVE</span>
          </Link>
          <div className="flex items-center gap-6">
            <span className="text-sm text-gray-400">
              Welcome, {currentUser.displayName || 'User'}
            </span>
            <button
              onClick={handleSignOut}
              className="flex items-center gap-2 bg-red-600/20 hover:bg-red-600/30 text-red-400 px-4 py-2 rounded-lg transition"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800">
            <div className="text-gray-400 text-sm mb-2">User Role</div>
            <div className="text-3xl font-black text-orange-500 capitalize">
              {userRole || 'Member'}
            </div>
          </div>
          <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800">
            <div className="text-gray-400 text-sm mb-2">Email</div>
            <div className="text-lg font-semibold">{currentUser.email}</div>
          </div>
          <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800">
            <div className="text-gray-400 text-sm mb-2">Status</div>
            <div className="text-2xl font-black text-green-500">Active</div>
          </div>
        </div>

        {/* Display Name Card */}
        <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800 mb-12">
          <div className="flex justify-between items-center">
            <div>
              <div className="text-gray-400 text-sm mb-2">Display Name</div>
              <div className="text-2xl font-black text-orange-500">
                {currentUser.displayName || 'Not Set'}
              </div>
              {!canChangeDisplayName && nextChangeDate && (
                <p className="text-xs text-gray-400 mt-2">
                  Next change available: {nextChangeDate.toLocaleDateString()}
                </p>
              )}
              {canChangeDisplayName && (
                <p className="text-xs text-green-400 mt-2">
                  You can change your display name now
                </p>
              )}
            </div>
            <button
              onClick={() => setShowChangeDisplayName(true)}
              disabled={!canChangeDisplayName}
              className="flex items-center gap-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg transition"
            >
              <Edit2 size={18} />
              Change Display Name
            </button>
          </div>
        </div>

        {/* Welcome Card */}
        <div className="bg-zinc-900 p-12 rounded-lg border border-zinc-800 text-center">
          <div className="text-7xl mb-6">🔥</div>
          <h1 className="text-4xl font-black mb-4">
            Welcome, {currentUser.displayName || 'User'} to Fire Hive Network
          </h1>
          <p className="text-gray-400 max-w-2xl mx-auto mb-8">
            You've successfully joined the network. Your journey to influence across the 7 mountains
            starts here. More features coming soon as the platform develops!
          </p>
          <button
            onClick={() => navigate('/')}
            className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 rounded-full font-bold transition"
          >
            Back to Home
          </button>
        </div>

        {/* Change Display Name Modal */}
        {showChangeDisplayName && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
            <div className="bg-zinc-900 p-8 rounded-lg border border-zinc-800 max-w-sm w-full mx-4">
              <h3 className="text-2xl font-bold mb-4">Change Display Name</h3>
              <p className="text-gray-400 mb-4">
                Choose a new display name (3-30 characters). You can change it again after 30 days.
              </p>

              {displayNameError && (
                <div className="bg-red-500/20 border border-red-500/50 text-red-300 p-4 rounded-lg mb-4">
                  {displayNameError}
                </div>
              )}

              <input
                type="text"
                value={newDisplayName}
                onChange={(e) => setNewDisplayName(e.target.value)}
                placeholder="Your new display name"
                className="w-full px-4 py-2 bg-zinc-800 border border-zinc-700 rounded-lg focus:border-orange-500 outline-none transition mb-4"
              />

              <div className="flex gap-3">
                <button
                  onClick={handleChangeDisplayName}
                  disabled={displayNameLoading}
                  className="flex-1 bg-orange-600 hover:bg-orange-700 disabled:opacity-50 text-white font-bold py-2 rounded-lg transition"
                >
                  {displayNameLoading ? 'Updating...' : 'Update Display Name'}
                </button>
                <button
                  onClick={() => {
                    setShowChangeDisplayName(false);
                    setNewDisplayName('');
                    setDisplayNameError('');
                  }}
                  className="flex-1 bg-zinc-800 hover:bg-zinc-700 text-white font-bold py-2 rounded-lg transition"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
